/**
 * useRunQueue — one data source for both run-queue views.
 *
 * Polls GET /rsa/v2/queue (held/pending/active/done + fleet + policy + waiting
 * reason) and GET /rsa/v2/fleet (last controller tick with per-run GPU phase)
 * on the same cadence as the task list (60 s), and exposes the queue actions
 * (cancel / bump / requeue / reorder / start). Runs are matched to queue
 * entries by run_id (see runQueueFormat).
 *
 * `projectIds` (array | null): when set, both GETs carry ?project_ids=… so a
 * non-admin only sees entries of the projects they can open. This is a
 * client-enforced view filter, not authorization — pass null for admins.
 *
 * `since` / `mine` / `limit` are the HISTORY filters (they only narrow the
 * `done` list): since = "today" (default) | "week" | "all"; mine = an email.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import runQueueService from "../services/runQueueService";
import {
  indexQueueByRunId,
  findQueueEntryForRun,
  bumpPriorityFor,
  projectIdsParam,
  scopeToProjects,
} from "../components/Sidebar/RunQueue/runQueueFormat";

export const useRunQueue = ({
  enabled = true,
  pollMs = 60000,
  includeDone = true,
  projectIds = null,
  since = null,
  mine = null,
  limit = null,
  api = runQueueService,
} = {}) => {
  // Re-fetch when the SET of ids changes, not when the caller hands us a new
  // array instance with the same content on every render.
  const projectIdsKey = projectIdsParam(projectIds);
  const scopedIds = useMemo(
    () => (projectIdsKey ? projectIdsKey.split(",") : null),
    [projectIdsKey],
  );
  const [queue, setQueue] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const [queueResult, fleetResult] = await Promise.allSettled([
        api.fetchQueue({ includeDone, projectIds: scopedIds, since, mine, limit }),
        api.fetchFleet({ projectIds: scopedIds }),
      ]);
      if (!mountedRef.current) return;
      if (queueResult.status === "fulfilled") {
        // Never trust the server to have applied project_ids (an older Pipeline ignores it).
        setQueue(scopeToProjects(queueResult.value, scopedIds));
        setError(null);
      } else {
        setError(queueResult.reason?.message || "Failed to load run queue");
      }
      if (fleetResult.status === "fulfilled") setFleet(scopeToProjects(fleetResult.value, scopedIds));
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled, includeDone, api, scopedIds, since, mine, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, Math.max(10000, pollMs));
    return () => clearInterval(timer);
  }, [enabled, pollMs, refresh]);

  const withBusy = useCallback(
    async (entryId, fn) => {
      setBusyIds((prev) => new Set(prev).add(entryId));
      try {
        const result = await fn();
        await refresh();
        return result;
      } finally {
        if (mountedRef.current) {
          setBusyIds((prev) => {
            const next = new Set(prev);
            next.delete(entryId);
            return next;
          });
        }
      }
    },
    [refresh],
  );

  const cancel = useCallback(
    (entry) => withBusy(entry.id, () => api.cancelQueued(entry.id)),
    [api, withBusy],
  );
  const bump = useCallback(
    (entry) =>
      withBusy(entry.id, () =>
        api.setQueuedPriority(entry.id, bumpPriorityFor(entry, queue?.pending)),
      ),
    [api, withBusy, queue],
  );
  const requeue = useCallback(
    (entry) => withBusy(entry.id, () => api.requeueEntry(entry.id)),
    [api, withBusy],
  );

  // A list action (reorder / start): every listed row is marked busy until the
  // refresh lands so the arrows/drag handles cannot fire a second, conflicting
  // call.
  const withListBusy = useCallback(
    async (ids, fn) => {
      const list = (ids || []).map(String).filter(Boolean);
      if (list.length === 0) return null;
      setBusyIds((prev) => {
        const next = new Set(prev);
        for (const id of list) next.add(id);
        return next;
      });
      try {
        const result = await fn(list);
        await refresh();
        return result;
      } finally {
        if (mountedRef.current) {
          setBusyIds((prev) => {
            const next = new Set(prev);
            for (const id of list) next.delete(id);
            return next;
          });
        }
      }
    },
    [refresh],
  );

  // Explicit ordering: `ids` (first = next to start) becomes the admission
  // order in one POST (held ids are ordered within the held list).
  const reorder = useCallback(
    (ids) => withListBusy(ids, (list) => api.reorderQueue(list)),
    [api, withListBusy],
  );
  // Start held entries: `ids` (first = next to start) become queued in that order.
  const start = useCallback(
    (ids) => withListBusy(ids, (list) => api.startQueued(list)),
    [api, withListBusy],
  );

  const index = useMemo(() => indexQueueByRunId(queue), [queue]);
  const entryForRun = useCallback((run) => findQueueEntryForRun(run, index), [index]);

  return {
    queue,
    fleet,
    loading,
    error,
    refresh,
    cancel,
    bump,
    requeue,
    reorder,
    start,
    busyIds,
    entryForRun,
    queueIndex: index,
    projectIds: scopedIds,
  };
};

export default useRunQueue;
